import React, { useState } from 'react';
import { Clock, CalendarDays } from 'lucide-react';
import clinicPhoto1 from '../../assets/clinicPhoto1.jpg';
import clinicPhoto2 from '../../assets/clinicPhoto2.jpg';
import clinicPhoto3 from '../../assets/clinicPhoto3.jpg';
import clinicPhoto4 from '../../assets/clinicPhoto4.jpg';
import Map from './Map';
import { Link } from 'react-router';

const VisitUsSection = () => {
  const [activeDay, setActiveDay] = useState('Monday');
  
  const weekdays = [
    { day: 'Monday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Tuesday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Wednesday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Thursday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Friday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Saturday', hours: '9AM-1PM, 4PM-8PM' },
    { day: 'Sunday', hours: 'Closed' }
  ];

  // Find the current day of week
  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentTime = new Date();
    const currentOffset = currentTime.getTimezoneOffset();
    const ISTOffset = 330;   // IST offset UTC +5:30 
    const ISTTime = new Date(currentTime.getTime() + (ISTOffset + currentOffset)*60000);
    const today = ISTTime.getDay();
    return days[today];
  };

  // Check if the clinic is currently open
  const isClinicOpen = () => {
    const currentDay = getCurrentDay();
    const currentTime = new Date();
    const currentOffset = currentTime.getTimezoneOffset();
    const ISTOffset = 330;   // IST offset UTC +5:30 
    const ISTTime = new Date(currentTime.getTime() + (ISTOffset + currentOffset)*60000);
    const currentHour = ISTTime.getHours()
    
    // Return false if Sunday
    if (currentDay === 'Sunday') return false;
    
    // Check if within opening hours for other days
    return (currentHour >= 9 && currentHour < 13) || (currentHour >= 16 && currentHour < 20);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 py-16 bg-gradient-to-r from-teal-50 to-green-50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {/* Image Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg overflow-hidden shadow-md h-64">
            <img src={clinicPhoto1} alt="Waiting area with blue chairs" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-lg overflow-hidden shadow-md h-64">
            <img src={clinicPhoto2} alt="Treatment room" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-lg overflow-hidden shadow-md h-64">
            <img src={clinicPhoto3} alt="Neon sign" className="w-full h-full object-cover" />
          </div>
          <div className="rounded-lg overflow-hidden shadow-md h-64">
            <img src={clinicPhoto4} alt="Recovery area" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Text Content */}
        <div className="space-y-6 xl:ml-12 flex flex-col justify-center">
          <h2 className="text-4xl font-bold text-gray-700">Visit Us</h2>
          
          <div className="w-24 h-1 bg-lime-500"></div>
          
          <p className="text-gray-600">
            We're conveniently located in the Nilamber corporate park. Find us on the 2nd floor 
            of the building. Plenty of free parking available.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 text-teal-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-700">226, Nilamber corporate park, @Arena</p>
                <p className="text-gray-700">Opposite Hyatt Place, Nilamber Circle</p>
                <p className="text-gray-700">Gotri-Vasna Road</p>
                <p className="text-gray-700">Vadodara, Gujarat 390007, India</p>
                <Link 
                  target='_blank'
                  to="https://www.google.com/maps/place/Pristine+Smile/@22.3011357,73.1369405,17z/data=!4m16!1m9!3m8!1s0x395fc956227309d5:0x79a69c5171205095!2sPristine+Smile!8m2!3d22.3011308!4d73.1395154!9m1!1b1!16s%2Fg%2F11wm_tmsy_!3m5!1s0x395fc956227309d5:0x79a69c5171205095!8m2!3d22.3011308!4d73.1395154!16s%2Fg%2F11wm_tmsy_?entry=ttu&g_ep=EgoyMDI1MDkwOS4wIKXMDSoASAFQAw%3D%3D"
                  className="mt-2 text-teal-600 hover:text-lime-600 transition-colors font-medium">
                  Get Directions
                </Link>
              </div>
            </div>

            {/* Innovative Clinic Hours Display */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={20} className="text-teal-600" />
                <h3 className="text-xl font-semibold text-gray-700">Clinic Hours</h3>
                <div className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${isClinicOpen() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {isClinicOpen() ? 'Open Now' : 'Closed Now'}
                </div>
              </div>
              
              {/* Interactive Calendar Strip */}
              <div className="flex overflow-x-auto pb-2 mb-4 -mx-2">
                {weekdays.map((item) => (
                  <div 
                    key={item.day}
                    onClick={() => setActiveDay(item.day)}
                    className={`flex-shrink-0 flex flex-col items-center px-4 py-2 mx-1 rounded-lg cursor-pointer transition-colors ${
                      activeDay === item.day 
                        ? 'bg-teal-500 text-white' 
                        : 'bg-gray-100 hover:bg-teal-100 text-gray-700'
                    }`}
                  >
                    <CalendarDays size={16} className={activeDay === item.day ? 'text-white' : 'text-teal-600'} />
                    <span className="text-sm font-medium">{item.day.substring(0, 3)}</span>
                  </div>
                ))}
              </div>
              
              {/* Selected Day Hours */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">{activeDay}</span>
                  <span className={`${activeDay === 'Sunday' ? 'text-red-500 font-semibold' : 'text-teal-600'}`}>
                    {weekdays.find(d => d.day === activeDay)?.hours}
                  </span>
                </div>
                
                {activeDay !== 'Sunday' && (
                  <div className="mt-3">
                    <div className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div className="text-xs text-gray-500">Morning</div>
                        <div className="text-xs text-gray-500">Afternoon</div>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                        <div className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-teal-500 w-1/3"></div>
                        <div className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gray-300 w-1/3"></div>
                        <div className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-lime-500 w-1/3"></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>9AM</span>
                        <span>1PM</span>
                        <span>4PM</span>
                        <span>8PM</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <Link to='/schedule-appointment' className="cursor-pointer mt-6 self-start bg-teal-500 hover:bg-lime-500 text-white px-6 py-2 rounded transition-colors font-medium">
            Book Appointment
          </Link>
          {/* <button className="mt-6 self-start bg-teal-500 hover:bg-lime-500 text-white px-6 py-2 rounded transition-colors font-medium">
          </button> */}
        </div>
      </div>

      <Map />
    </div>
  );
};

export default VisitUsSection;